import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Row } from 'react-bootstrap'
import ChartOptions from '../ChartOptions'
import ChartPreview from '../ChartPreview'
import { getSampledRows } from '../../services/largeDataset'
import useMappedData from '../../hooks/useMappedData'

const ChartPreviewWithOptions = ({
  chart,
  dataset,
  dataTypes,
  mapping,
  visualOptions,
  setVisualOptions,
  setRawViz,
  datasetRowCount = 0,
  previewRendererMode = { mode: 'svg', message: null },
  loading = false,
  setMappingLoading,
  brandTheme,
  chartColorSyncMode = 'brand',
  hasMappedColorField = false,
  showOptions = true,
  showPreview = true,
  beforeOptionsContent,
  chartGroupContent,
  axisGroupContent,
  optionGroupFilter,
  optionsClassName,
  hideOptionGroupChrome = false,
  modernOptionGroupChrome = false,
  previewClassName,
  optionsEmptyState,
  renderSource = 'unknown',
  enableAutoMargin = true,
  useDeclaredSvgViewport = false,
  onLayoutDiagnostics = null,
}) => {
  const [error, setError] = useState(null)

  const previewDataset = React.useMemo(() => {
    const previewSampleRows = previewRendererMode?.previewSampleRows
    return previewSampleRows ? getSampledRows(dataset, previewSampleRows) : dataset
  }, [dataset, previewRendererMode])

  const { mappedData, loadingMessage, normalizedMapping, mappingError } = useMappedData({
    chart,
    dataset: previewDataset,
    dataTypes,
    mapping,
    datasetRowCount,
    setMappingLoading,
  })

  // Auto-margin: allow up to 3 correction passes per chart+data combination
  // so margins converge (each pass only ever increases a value, guaranteeing
  // termination), but reset the counter when the chart or dataset changes.
  const autoMarginCount = useRef(0)
  useEffect(() => {
    autoMarginCount.current = 0
  }, [chart?.metadata?.id, mappedData])

  const handleAutoMargin = useCallback((suggestions) => {
    if (!enableAutoMargin) return
    if (autoMarginCount.current >= 3) return
    setVisualOptions((prev) => {
      const updates = Object.fromEntries(
        Object.entries(suggestions).filter(
          ([key, val]) => val > (Number(prev[key]) || 0)
        )
      )
      if (!Object.keys(updates).length) return prev
      autoMarginCount.current += 1
      return { ...prev, ...updates }
    })
  }, [enableAutoMargin, setVisualOptions])

  return (
    <Row>
      {showOptions && (
        <>
          {beforeOptionsContent}
          <ChartOptions
            chart={chart}
            dataset={dataset}
            mapping={mapping}
            dataTypes={dataTypes}
            visualOptions={visualOptions}
            setVisualOptions={setVisualOptions}
            error={error}
            mappedData={mappedData}
            groupFilter={optionGroupFilter}
            className={optionsClassName}
            hideGroupChrome={hideOptionGroupChrome}
            modernGroupChrome={modernOptionGroupChrome}
            emptyState={optionsEmptyState}
            chartGroupContent={chartGroupContent}
            axisGroupContent={axisGroupContent}
          />
        </>
      )}
      {showPreview && (
        <ChartPreview
          chart={chart}
          dataset={dataset}
          dataTypes={dataTypes}
          mapping={mapping}
          visualOptions={visualOptions}
          brandTheme={brandTheme}
          chartColorSyncMode={chartColorSyncMode}
          hasMappedColorField={hasMappedColorField}
          loadingMessage={loadingMessage}
          loading={loading}
          datasetRowCount={datasetRowCount}
          previewRendererMode={previewRendererMode}
          error={error}
          mappingError={mappingError}
          setError={setError}
          setRawViz={setRawViz}
          mappedData={mappedData}
          className={previewClassName}
          renderSource={renderSource}
          onAutoMargin={handleAutoMargin}
          onLayoutDiagnostics={onLayoutDiagnostics}
          useDeclaredSvgViewport={useDeclaredSvgViewport}
        />
      )}
    </Row>
  )
}

export default ChartPreviewWithOptions
